/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback } from 'react';
import { useStore } from '../../store';
import { GeoArea } from '../../types';

type UseGeoArea = (geoAreaId: GeoArea['id'] | null) => {
	getGeoAreaById: (id: GeoArea['id']) => GeoArea;
	getGeoAreasByParentId: (parentId: GeoArea['id'] | null) => GeoArea[];
	getGeoAreasByIds: (ids: GeoArea['id'][]) => GeoArea[];
	childrenIds: GeoArea['id'][];
	isIncluded: boolean;
	isExcluded: boolean;
};

const useGeoArea: UseGeoArea = geoAreaId => {
	const itemsById = useStore(useCallback(state => state.itemsById, []));
	const itemsByParentId = useStore(
		useCallback(state => (geoAreaId ? state.itemsByParentId[geoAreaId] : []), [geoAreaId])
	);
	const itemsFirstLevel = useStore(useCallback(state => state.itemsFirstLevel, []));

	const getGeoAreaById = useCallback((id: string) => itemsById?.[id], [itemsById]);

	const getGeoAreasByIds = useCallback(
		(ids: string[]) => {
			return ids?.map((id: string) => getGeoAreaById(id));
		},
		[getGeoAreaById]
	);

	const getGeoAreasByParentId = useCallback(
		(parentId: string | null): GeoArea[] => {
			if (parentId === null) {
				return itemsFirstLevel?.map((id: string) => itemsById?.[id]);
			}

			return itemsByParentId?.map((id: string) => itemsById?.[id]);
		},
		[itemsFirstLevel, itemsByParentId, itemsById]
	);

	const children = getGeoAreasByIds(itemsByParentId);

	const childrenToFetch =
		children?.filter((geoArea: GeoArea) => !geoArea.leaf)?.map((geoArea: GeoArea) => geoArea.id) || [];

	const isIncluded = useStore(
		useCallback(state => !!(geoAreaId && state?.includedGeoAreaIds.includes(geoAreaId)), [geoAreaId])
	);
	const isExcluded = useStore(
		useCallback(state => !!(geoAreaId && state?.excludedGeoAreaIds.includes(geoAreaId)), [geoAreaId])
	);

	return {
		getGeoAreaById,
		getGeoAreasByParentId,
		getGeoAreasByIds,
		childrenIds: childrenToFetch,
		isIncluded,
		isExcluded
	};
};

export default useGeoArea;
